import React, { useMemo } from 'react';

import { CellContext, ColumnDef } from '@tanstack/react-table';
import { Offer } from 'common/contracts';
import { DATE_TIME_FORMAT } from 'constants/constants';
import { SortingOffersValue } from 'constants/enums';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import ActionCell from 'components/DataTableComponent/components/ActionCell';
import SortableHeader from 'components/SortableHeader/SortableHeader';

import { IOfferColumnsBaseProps } from '../../../Offer.types';
import { renderOfferStatusChip } from '../../../Offer.utils';

dayjs.extend(utc);

interface IUseRewardCalendarColumnsProps extends IOfferColumnsBaseProps {
  hasFeatureFlagCheats: boolean;
  onOpenCheats: (offerId: string) => void;
}

interface IUseRewardCalendarColumnsReturn {
  columns: ColumnDef<Offer>[];
}

export const useRewardCalendarColumns = ({
  sorting,
  updateSorting,
  isLoading,
  actions,
  permissions,
  hasFeatureFlagCheats,
  onOpenCheats
}: IUseRewardCalendarColumnsProps): IUseRewardCalendarColumnsReturn => {
  const columns = useMemo((): ColumnDef<Offer>[] => {
    const idColumn: ColumnDef<Offer> = {
      accessorKey: 'offerId',
      header: () => (
        <SortableHeader
          label="ID"
          sortValue={SortingOffersValue.ID}
          currentSortValue={sorting.sortValue}
          currentDirection={sorting.direction}
          onSort={updateSorting}
        />
      ),
      cell: (cell: CellContext<Offer, unknown>) => cell.getValue()
    };

    const externalIdColumn: ColumnDef<Offer> = {
      accessorKey: 'publisherOfferId',
      header: () => (
        <SortableHeader
          label="External ID"
          sortValue={SortingOffersValue.PUBLISHER_OFFER_ID}
          currentSortValue={sorting.sortValue}
          currentDirection={sorting.direction}
          onSort={updateSorting}
        />
      ),
      cell: (cell: CellContext<Offer, unknown>) => cell.getValue()
    };

    const nameColumn: ColumnDef<Offer> = {
      accessorKey: 'name',
      header: () => (
        <SortableHeader
          label="Name"
          sortValue={SortingOffersValue.NAME}
          currentSortValue={sorting.sortValue}
          currentDirection={sorting.direction}
          onSort={updateSorting}
        />
      ),
      cell: (cell: CellContext<Offer, unknown>) => cell.getValue()
    };

    const offerDesignColumn: ColumnDef<Offer> = {
      accessorKey: 'offerDesign',
      header: 'Offer Design',
      cell: ({ row }: CellContext<Offer, unknown>) => {
        const offerUi = row.original.offerUi;
        return offerUi?.name || offerUi?.offerUiId || '-';
      }
    };

    const weeksColumn: ColumnDef<Offer> = {
      accessorKey: 'weeks',
      header: 'Weeks',
      cell: ({ row }: CellContext<Offer, unknown>) => {
        const sequence =
          (row.original.productsSequence as Array<{ products: unknown[] }>) ||
          [];
        const daysWithProducts = sequence.filter(
          (item) => item.products.length > 0
        ).length;
        if (daysWithProducts === 0) return '-';
        return Math.ceil(daysWithProducts / 7);
      }
    };

    const priorityColumn: ColumnDef<Offer> = {
      accessorKey: 'priority',
      header: 'Priority',
      cell: (cell: CellContext<Offer, unknown>) => cell.getValue() ?? '-'
    };

    const statusColumn: ColumnDef<Offer> = {
      accessorKey: 'active',
      header: () => (
        <SortableHeader
          label="Status"
          sortValue={SortingOffersValue.STATUS}
          currentSortValue={sorting.sortValue}
          currentDirection={sorting.direction}
          onSort={updateSorting}
        />
      ),
      accessorFn: (row: Offer) => (row.active ? 'Active' : 'Inactive'),
      cell: ({ row }: CellContext<Offer, unknown>) =>
        renderOfferStatusChip(row.original)
    };

    const createdAtColumn: ColumnDef<Offer> = {
      accessorKey: 'createdAt',
      header: () => (
        <SortableHeader
          label="Created At"
          sortValue={SortingOffersValue.CREATED_AT}
          currentSortValue={sorting.sortValue}
          currentDirection={sorting.direction}
          onSort={updateSorting}
        />
      ),
      cell: ({ row }: CellContext<Offer, unknown>) => {
        if (isLoading) return '';
        return (
          <span className="whitespace-nowrap overflow-hidden text-ellipsis block">
            {dayjs(row.original.createdAt).utc().format(DATE_TIME_FORMAT)}
          </span>
        );
      }
    };

    const actionsColumn: ColumnDef<Offer> = {
      accessorKey: 'actions',
      header: '',
      maxSize: 60,
      minSize: 60,
      size: 60,
      cell: ({ row }: CellContext<Offer, unknown>) => {
        const { offerId } = row.original;
        if (!offerId) {
          return null;
        }
        return (
          <div className="max-w-[60px] w-[60px]">
            <ActionCell
              rowData={row.original}
              actions={[
                ...(hasFeatureFlagCheats
                  ? [
                      {
                        label: 'Cheats',
                        handler: () => onOpenCheats(offerId),
                        disabled:
                          !permissions.canUserEdit ||
                          !permissions.canManageOffers
                      }
                    ]
                  : []),
                {
                  label: row.original.active ? 'Deactivate' : 'Activate',
                  handler: () =>
                    actions.onToggleStatus(offerId, row.original.active),
                  disabled: !permissions.canUserEdit
                },
                {
                  label: 'Edit',
                  handler: () => actions.onEdit(offerId),
                  disabled:
                    !permissions.canUserEdit || !permissions.canManageOffers,
                  hidden: !permissions.canManageOffers
                },
                {
                  label: 'Edit',
                  handler: () => actions.onLimitedEdit(offerId),
                  disabled:
                    !permissions.canUserEdit ||
                    !permissions.canUpdateStatusDesign,
                  hidden:
                    !permissions.canUpdateStatusDesign ||
                    permissions.canManageOffers
                },
                {
                  label: 'Duplicate',
                  handler: () => actions.onDuplicate(offerId),
                  disabled:
                    !permissions.canUserEdit || !permissions.canManageOffers
                },
                {
                  label: 'Delete',
                  handler: () => actions.onDelete(offerId),
                  disabled:
                    !permissions.canUserEdit || !permissions.canManageOffers
                }
              ]}
            />
          </div>
        );
      }
    };

    return [
      idColumn,
      externalIdColumn,
      nameColumn,
      offerDesignColumn,
      weeksColumn,
      priorityColumn,
      statusColumn,
      createdAtColumn,
      actionsColumn
    ];
  }, [sorting, updateSorting, isLoading, actions, permissions, hasFeatureFlagCheats, onOpenCheats]);

  return { columns };
};
